import { useMemo } from "react"

import { useParams } from "@/lib/router"
import { useLang } from "@/context/useLang"
import { useTrans } from "@/lib/i18n"

import { CardWrapper, CardBody } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { MultiGraph } from "@/components/graphs/types/bar-line/MultiGraph"
import { GraphLabel, GraphLabels } from "@/components/graphs/GraphLabels"

import { TickRendererProps } from "@visx/axis"
import { Text } from "@visx/text"

import {
	StatsInformationByInterestPeriodType,
	useSingleProjectStatsInterestPeriodsQuery,
} from "@/api/graphql"

import { useLocalizedDateTime } from "@/lib/dates"

export function ProjectInterestByPeriodGraph() {
	const { formatCurrency } = useLang()
	const t = useTrans("dashboard")
	const DateTime = useLocalizedDateTime()
	const params = useParams()

	const { data } = useSingleProjectStatsInterestPeriodsQuery({
		id: String(params.id),
	})

	const stats = useMemo(
		() =>
			(data?.project_statistics?.stats_information_by_interest_period as
				| StatsInformationByInterestPeriodType[]
				| undefined) ?? [],
		[data?.project_statistics?.stats_information_by_interest_period],
	)

	const barData = useMemo(
		() => [
			{
				id: "expected_interest",
				variant: "default" as const,
				data: stats.map((item) => ({
					x: item?.end_date
						? String(DateTime.fromISO(item.end_date).year)
						: "0",
					y: Number(item?.expected_interest_amount ?? 0),
				})),
			},
			{
				id: "interest_paid",
				variant: "primary" as const,
				data: stats.map((item) => ({
					x: item?.end_date
						? String(DateTime.fromISO(item.end_date).year)
						: "0",
					y: Number(item?.total_actual_interest ?? 0),
				})),
			},
		],
		[stats, DateTime],
	)

	const prepareTooltipValues = useMemo(
		() => (year: string) => {
			const periodData = stats.find(
				(d) =>
					d?.end_date &&
					String(DateTime.fromISO(d.end_date).year) === year,
			)
			if (!periodData) return []

			return [
				{
					id: "interest_paid",
					value: formatCurrency(
						Number(periodData.total_actual_interest ?? 0),
						{ maximumFractionDigits: 0 },
					),
					variant: "primary" as const,
					colour: MultiGraph.barVariantColourMap.primary,
				},
				{
					id: "expected_interest",
					value: formatCurrency(
						Number(periodData.expected_interest_amount ?? 0),
						{ maximumFractionDigits: 0 },
					),
					variant: "default" as const,
					colour: MultiGraph.barVariantColourMap.default,
				},
			]
		},
		[stats, formatCurrency, DateTime],
	)

	const prepareTooltipLabel = useMemo(() => (year: string) => year, [])

	const YearTickComponent = useMemo(
		() =>
			function YearTick({ formattedValue, ...props }: TickRendererProps) {
				return <Text {...props}>{formattedValue}</Text>
			},
		[],
	)

	const CurrencyTickComponent = useMemo(
		() =>
			function CurrencyTick({
				formattedValue,
				...props
			}: TickRendererProps) {
				const value = parseFloat(formattedValue as string)
				const displayValue =
					value === 0 ? "0" : `${Math.round(value / 1000)}k`

				return <Text {...props}>{displayValue}</Text>
			},
		[],
	)

	return (
		<CardWrapper className="mt-5">
			<CardBody>
				<Heading as="h2" styleAs="h5" className="mb-6 text-gray-600">
					{t("dashboard:statistics.interest_by_year.title")}
				</Heading>
			</CardBody>
			<div className="h-96 w-full">
				<MultiGraph
					barDatas={barData}
					groupedBars
					prepareTooltipValues={prepareTooltipValues}
					prepareTooltipLabel={prepareTooltipLabel}
					yTickComponent={YearTickComponent}
					xTickComponent={CurrencyTickComponent}
				/>
			</div>
			<GraphLabels className="p-3 px-4 sm:p-5 sm:px-6">
				<GraphLabel
					type="bar"
					variant="primary"
					title={t("common:multi_graph.tooltip.interest_paid")}
				/>
				<GraphLabel
					type="bar"
					variant="default"
					title={t("common:multi_graph.tooltip.expected_interest")}
				/>
			</GraphLabels>
		</CardWrapper>
	)
}
